import Checkbox from "@/v2-ui/checkbox"
import Label from "@/v2-ui/label"
import cn from "@/v2-ui/utils/utils.cn"
import Badge from "@/v2-backoffice-client/components/badge"
import LicenseFeatureGroupCheckedByMark from "@/v2-backoffice-client/license/featureGroup/compose/LicenseFeatureGroupCheckedByMark"
import LicenseFeatureGroupDetailsHoverCard from "@/v2-backoffice-client/license/featureGroup/compose/LicenseFeatureGroupDetailsHoverCard"
import {
  checkedByOf,
  describeCheckedBy,
} from "@/v2-backoffice-client/license/featureGroup/compose/license.featureGroup.checkedBy.utils"
import type { FeatureGroupPickedLike } from "@/v2-backoffice-client/license/featureGroup/compose/license.featureGroup.compose.utils"
import {
  effectOf,
  toPicked,
  type RegistryRow,
} from "@/v2-backoffice-client/license/featureGroup/compose/registry/license.featureGroup.registry.utils"

export type LicenseFeatureGroupRegistryFeatureRowProps = {
  row: RegistryRow
  /** The content types the map checks (base maps, map layers). */
  entitledTypeCodes: ReadonlySet<number>
  /** Rows of several types are listed together (a filter with no type picked): name the type on each. */
  showsType: boolean
  featureGroupName: string
  isPicked(code: string): boolean
  wasPicked(code: string): boolean
  onSetPicked(
    features: readonly FeatureGroupPickedLike[],
    picked: boolean,
  ): void
}

// One registry feature in the third column: the checkbox is the pick
// itself (a surface lands in Capabilities, content in Content, anything
// else in the individual list), the hover card says what picking it does,
// and the row says the one thing worth knowing at a glance — that it is a
// capability, which server checks it, or that nothing checks it yet.
function LicenseFeatureGroupRegistryFeatureRow(
  props: LicenseFeatureGroupRegistryFeatureRowProps,
) {
  const {
    row,
    entitledTypeCodes,
    showsType,
    featureGroupName,
    isPicked,
    wasPicked,
    onSetPicked,
  } = props
  const checkedBy = checkedByOf({
    isSurface: row.IsSurface,
    featureTypeCode: row.FeatureTypeCode,
    entitledTypeCodes,
    checkedBy: row.CheckedBy,
    checkedHow: row.CheckedHow,
  })
  const picked = isPicked(row.Code)
  const isChanged = picked !== wasPicked(row.Code)
  const otherPackages = (row.FeatureGroupNames ?? "")
    .split(", ")
    .filter((name) => name && name !== featureGroupName)

  return (
    <li
      className={cn(
        "border-l-2 border-l-transparent",
        isChanged && "border-l-primary bg-primary/5",
      )}
    >
      <Label className="flex cursor-pointer items-center gap-2.5 py-1 pl-2.5 pr-2 font-normal">
        <Checkbox
          checked={picked}
          onCheckedChange={(checked) => {
            onSetPicked([toPicked(row)], checked === true)
          }}
        />
        <LicenseFeatureGroupDetailsHoverCard
          title={row.Name ?? row.Code}
          code={row.Code}
          description={row.Description}
          facts={[
            { label: "Kind", value: row.FeatureTypeName },
            { label: "Checked by", value: describeCheckedBy(checkedBy) },
            { label: "Picked here", value: effectOf(row, checkedBy) },
            {
              label: "Carried by",
              value: row.FeatureGroupNames || "no package yet",
            },
          ]}
        >
          <span className="min-w-0 flex-1">
            <span className="block truncate text-xs">
              {row.Name ?? row.Code}
            </span>
            <span className="block truncate font-mono text-[10px] text-muted-foreground">
              {row.Code}
            </span>
          </span>
        </LicenseFeatureGroupDetailsHoverCard>
        {isChanged && (
          <span className="shrink-0 text-[11px] text-primary">
            {picked ? "added" : "removed"}
          </span>
        )}
        {row.IsSurface ? (
          <Badge size="xs" variant="outline">
            Capability
          </Badge>
        ) : (
          <>
            <LicenseFeatureGroupCheckedByMark checkedBy={checkedBy} />
            {showsType && (
              <Badge size="xs" variant="outline">
                {row.FeatureTypeName}
              </Badge>
            )}
          </>
        )}
        {otherPackages.length > 0 && (
          <span className="max-w-28 shrink-0 truncate text-[11px] text-muted-foreground">
            {otherPackages.join(", ")}
          </span>
        )}
      </Label>
    </li>
  )
}

export default LicenseFeatureGroupRegistryFeatureRow
